import { reactive } from 'vue'
import { EventType } from '@azure/msal-browser'

import { getUserPermissionsAsync } from './getUserPermissions'

const permissionsCacheKeyPrefix = 'pocro-vento:userPermissions:'
const networkErrorCodes = new Set([
  'get_request_failed',
  'no_network_connectivity',
  'post_request_failed',
])
let pendingPermissionsRequest = null
let installedMsalState = null

function getPermissionsCacheKey (accountId) {
  return `${permissionsCacheKeyPrefix}${accountId}`
}

function readCachedPermissions (accountId) {
  try {
    const cachedPermissions = localStorage.getItem(getPermissionsCacheKey(accountId))
    const parsedPermissions = cachedPermissions ? JSON.parse(cachedPermissions) : null

    if (
      !Array.isArray(parsedPermissions?.roles) ||
      !Array.isArray(parsedPermissions?.permissions) ||
      !Array.isArray(parsedPermissions?.limitedBrandCodes)
    ) {
      return null
    }

    return parsedPermissions
  } catch (error) {
    console.error('Failed to read cached user permissions.', error)
    return null
  }
}

function writeCachedPermissions (accountId, permissions) {
  try {
    localStorage.setItem(getPermissionsCacheKey(accountId), JSON.stringify(permissions))
  } catch (error) {
    console.error('Failed to cache user permissions.', error)
  }
}

function clearCachedPermissions (accountId) {
  try {
    localStorage.removeItem(getPermissionsCacheKey(accountId))
  } catch (error) {
    console.error('Failed to clear cached user permissions.', error)
  }
}

function isOfflineOrNetworkError (error) {
  return !error?.status && (
    navigator.onLine === false ||
    error instanceof TypeError ||
    error?.name === 'AbortError' ||
    networkErrorCodes.has(error?.errorCode)
  )
}

export function getMsalState () {
  return installedMsalState
}

function setPermissions (state, permissions, accountId) {
  state.permissions = permissions
  state.permissionsAccountId = accountId
}

function setEmptyPermissions (state, accountId) {
  setPermissions(state, {
    roles: [],
    permissions: [],
    limitedBrandCodes: [],
  }, accountId)
}

export function clearPermissions (state, clearCache = false) {
  if (clearCache && state.permissionsAccountId) {
    clearCachedPermissions(state.permissionsAccountId)
  }

  state.permissions = null
  state.permissionsAccountId = null
}

export async function refreshPermissionsAsync (state) {
  const account = state.instance.getActiveAccount()

  if (!account) {
    clearPermissions(state, true)
    return
  }

  const accountId = account.homeAccountId

  if (state.permissionsAccountId && state.permissionsAccountId !== accountId) {
    clearPermissions(state, true)
  }

  state.permissionsAccountId = accountId

  if (pendingPermissionsRequest?.accountId === accountId) {
    return pendingPermissionsRequest.promise
  }

  const requestPromise = (async () => {
    try {
      const permissions = await getUserPermissionsAsync()

      if (!permissions || state.instance.getActiveAccount()?.homeAccountId !== accountId) {
        return
      }

      setPermissions(state, permissions, accountId)
      writeCachedPermissions(accountId, permissions)
    } catch (error) {
      if ([401, 403].includes(error?.status)) {
        clearCachedPermissions(accountId)
      } else if (isOfflineOrNetworkError(error)) {
        const cachedPermissions = readCachedPermissions(accountId)

        if (cachedPermissions && state.instance.getActiveAccount()?.homeAccountId === accountId) {
          setPermissions(state, cachedPermissions, accountId)
          return
        }
      }

      console.error('Failed to load user permissions.', error)

      if (state.instance.getActiveAccount()?.homeAccountId === accountId) {
        setEmptyPermissions(state, accountId)
      }
    }
  })()

  pendingPermissionsRequest = {
    accountId,
    promise: requestPromise,
  }

  try {
    await requestPromise
  } finally {
    if (pendingPermissionsRequest?.promise === requestPromise) {
      pendingPermissionsRequest = null
    }
  }
}

export async function ensurePermissionsAsync (state) {
  const accountId = state.instance.getActiveAccount()?.homeAccountId

  if (!accountId || (
    state.permissionsAccountId === accountId &&
    state.permissions !== null
  )) {
    return
  }

  await refreshPermissionsAsync(state)
}

/**
 * Helper function to determine whether 2 arrays are equal
 * Used to avoid unnecessary state updates
 * @param arrayA
 * @param arrayB
 */
function accountArraysAreEqual (arrayA, arrayB) {
  if (arrayA.length !== arrayB.length) {
    return false
  }
  const comparisonArray = [...arrayB]
  return arrayA.every((elementA) => {
    const elementB = comparisonArray.shift()
    if (!elementA || !elementB) {
      return false
    }
    return elementA.homeAccountId === elementB.homeAccountId &&
      elementA.localAccountId === elementB.localAccountId &&
      elementA.username === elementB.username
  })
}

// Exposes the MSAL instance, accounts and permissions as reactive Vue state,
// available in any component via useMsal()/useIsAuthenticated().
export const msalPlugin = {
  install (app, msalInstance) {
    const state = reactive({
      instance: msalInstance,
      accounts: msalInstance.getAllAccounts(),
      permissions: null,
      permissionsAccountId: null,
    })

    installedMsalState = state
    app.config.globalProperties.$msal = state

    refreshPermissionsAsync(state).catch((error) => {
      console.error('Failed to initialize user permissions.', error)
    })

    msalInstance.addEventCallback((message) => {
      switch (message.eventType) {
        case EventType.ACCOUNT_ADDED:
        case EventType.ACCOUNT_REMOVED:
        case EventType.LOGIN_SUCCESS:
        case EventType.SSO_SILENT_SUCCESS:
        case EventType.LOGOUT_SUCCESS:
        case EventType.LOGOUT_END:
        case EventType.HANDLE_REDIRECT_END:
        case EventType.LOGIN_FAILURE:
        case EventType.SSO_SILENT_FAILURE:
        case EventType.ACQUIRE_TOKEN_SUCCESS:
        case EventType.ACQUIRE_TOKEN_FAILURE: {
          const currentAccounts = msalInstance.getAllAccounts()
          const accountsChanged = !accountArraysAreEqual(currentAccounts, state.accounts)
          const activeAccountChanged = msalInstance.getActiveAccount()?.homeAccountId !==
            state.permissionsAccountId

          if (accountsChanged) {
            state.accounts = currentAccounts
          }

          if (currentAccounts.length === 0) {
            clearPermissions(state, true)
          } else if (accountsChanged || activeAccountChanged) {
            refreshPermissionsAsync(state).catch((error) => {
              console.error('Failed to refresh user permissions after an account change.', error)
            })
          }
          break
        }
      }
    })
  },
}
